import React from 'react';
import {
  Box,
  Typography,
  Badge,
  Collapse,
  makeStyles,
} from '@material-ui/core';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { TemplateContextProvider, useTemplateContextInfo } from '@internal/backstage-plugin-global-header';

// re-export for consumers that still import from this file
export { TemplateContextProvider as AgentChatTemplateProvider, useTemplateContextInfo as useAgentChatTemplateInfo };
export type { TemplateContextInfo as AgentChatTemplateInfo } from '@internal/backstage-plugin-global-header';
export function useAgentChatTemplatePath(): string | null {
  return useTemplateContextInfo().derivedPath;
}

const useStyles = makeStyles({
  navItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    color: '#4a4a4a',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s',
    textDecoration: 'none',
    position: 'relative',
    borderLeft: '3px solid transparent',
    '&:hover': {
      backgroundColor: 'rgba(26,35,50,0.05)',
    },
  },
  navItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  navItemActive: {
    color: '#1a2332',
    backgroundColor: 'rgba(26,35,50,0.08)',
    borderLeft: '3px solid #1a2332',
  },
  navIcon: {
    fontSize: '24px',
  },
  navText: {
    fontSize: '14px',
    fontWeight: 500,
  },
  expandIcon: {
    fontSize: '20px',
  },
  subItem: {
    padding: '10px 16px 10px 52px',
    position: 'relative',
  },
  subItemActive: {
    backgroundColor: 'rgba(26,35,50,0.08)',
    borderLeft: '3px solid #1a2332',
  },
});

interface STSNavItemProps {
  icon?: React.ComponentType<any>;
  text: string;
  to?: string;
  badge?: number;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
  isSubItem?: boolean;
  active?: boolean;
}

export const STSNavItem = ({
  icon: Icon,
  text,
  to,
  badge,
  expandable,
  isExpanded,
  onToggle,
  children,
  isSubItem,
  active,
}: STSNavItemProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { derivedPath } = useTemplateContextInfo();

  const fromPath =
    (location.state as any)?.from ??
    ((location.pathname.includes('/sts/create/templates/') ||
      location.pathname.includes('/sts/agent-chat/'))
      ? derivedPath
      : null) ??
    (location.pathname.includes('/sts/create/tasks/')
      ? (sessionStorage.getItem('sts_template_from') ?? '/sts/task-dashboard')
      : null);

  const isActive = active !== undefined ? active : (to
    ? location.pathname.startsWith(`/${to}`) ||
      ((location.pathname.startsWith('/sts/create') ||
        location.pathname.startsWith('/sts/agent-chat')) &&
        fromPath?.startsWith(`/${to}`))
    : false);

  const handleClick = () => {
    if (expandable && onToggle) {
      onToggle();
    } else if (to) {
      navigate(`/${to}`);
    }
  };

  return (
    <>
      <Box
        className={`${classes.navItem} ${
          isActive ? classes.navItemActive : ''
        } ${isSubItem ? classes.subItem : ''} ${
          isSubItem && isActive ? classes.subItemActive : ''
        }`}
        onClick={handleClick}
      >
        <Box className={classes.navItemContent}>
          {Icon &&
            (badge ? (
              <Badge badgeContent={badge} color="error">
                <Icon
                  className={classes.navIcon}
                  style={isSubItem ? { fontSize: '20px' } : {}}
                />
              </Badge>
            ) : (
              <Icon
                className={classes.navIcon}
                style={isSubItem ? { fontSize: '20px' } : {}}
              />
            ))}
          <Typography className={classes.navText}>{text}</Typography>
        </Box>
        {expandable &&
          (isExpanded ? (
            <ExpandLessIcon className={classes.expandIcon} />
          ) : (
            <ExpandMoreIcon className={classes.expandIcon} />
          ))}
      </Box>
      {expandable && (
        <Collapse in={isExpanded} style={{ position: 'relative', zIndex: 0 }}>
          <Box>{children}</Box>
        </Collapse>
      )}
    </>
  );
};
